import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {AbstractControl} from "@angular/forms";
import {Subscription} from "rxjs";

interface LanguageViewModel {
  code: string;
  icon: string;
  filled: boolean;
  required: boolean;   // required AND not filled
  invalid: boolean;
  showCheckmark: boolean;        // invalid && !required && filled
  showNotCheckedIndicator: boolean; // invalid && !required
}

@Component({
  selector: 'app-language-input-switcher',
  templateUrl: './language-input-switcher.component.html',
  styleUrls: ['./language-input-switcher.component.scss'],
  standalone: false
})
export class LanguageInputSwitcherComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedLanguage: string = "hr";
  @Input() defaultLanguage: string | undefined;
  @Output() languageSelected: EventEmitter<string> = new EventEmitter<string>();

  @Input() hrControl: AbstractControl | undefined;
  @Input() enControl: AbstractControl | undefined;
  @Input() itControl: AbstractControl | undefined;
  @Input() sloControl: AbstractControl | undefined;
  @Input() srControl: AbstractControl | undefined;
  @Input() bsControl: AbstractControl | undefined;

  private static readonly ALL_LANGUAGES: string[] = ["hr", "en", "it", "slo", "sr", "bs"];
  private static readonly LANGUAGE_ICONS: { [key: string]: string } = {
    'hr': 'ioc-CRO',
    'en': 'ioc-GBR',
    'it': 'ioc-ITA',
    'slo': 'ioc-SLO',
    'sr': 'ioc-SRB',
    'bs': 'ioc-BIH'
  };

  orderOfLanguages: string[] = LanguageInputSwitcherComponent.ALL_LANGUAGES;
  languageViewModels: LanguageViewModel[] = [];

  private controlSubscriptions: Subscription[] = [];

  ngOnInit() {
    this.initFields();
    this.subscribeToControls();
    this.rebuildViewModels();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (Object.values(changes).every(c => c.firstChange)) {
      return;
    }
    if (changes['defaultLanguage']) {
      this.initFields();
    }
    const controlInputsChanged = ['hrControl', 'enControl', 'itControl', 'sloControl', 'srControl', 'bsControl']
        .some((key: string) => changes[key] && !changes[key].firstChange);
    if (controlInputsChanged) {
      this.subscribeToControls();
    }
    this.rebuildViewModels();
  }

  ngOnDestroy() {
    this.controlSubscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  private initFields() {
    if (this.selectedLanguage === undefined || this.selectedLanguage === '') {
      this.selectedLanguage = this.defaultLanguage ?? "hr";
    }
    const first = this.defaultLanguage ?? "hr";
    this.orderOfLanguages = [first].concat(
        LanguageInputSwitcherComponent.ALL_LANGUAGES.filter((lang: string) => lang !== first));
  }

  private subscribeToControls(): void {
    this.controlSubscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.controlSubscriptions = [];

    this.orderOfLanguages.forEach((code: string) => {
      const control = this.getControl(code);
      if (!control) {
        return;
      }
      this.controlSubscriptions.push(
          control.valueChanges.subscribe(() => this.rebuildViewModels()));
      this.controlSubscriptions.push(
          control.statusChanges.subscribe(() => this.rebuildViewModels()));
    });
  }

  private rebuildViewModels() {
    this.languageViewModels = this.orderOfLanguages.map((code: string) => {
      const control = this.getControl(code);
      const filled = !!control?.value;
      const invalid = !!control?.errors;
      const required = !filled && !!control?.errors?.['required'];
      return {
        code,
        icon: LanguageInputSwitcherComponent.LANGUAGE_ICONS[code],
        filled,
        required,
        invalid,
        showCheckmark: !invalid && !required && filled,
        showNotCheckedIndicator: invalid && !required,
      };
    });
  }

  setLanguage(event: Event, language: string) {
    event.stopPropagation();
    this.languageSelected.emit(language);
    this.selectedLanguage = language;
  }

  private getControl(language: string): AbstractControl | undefined {
    switch (language) {
      case "hr": return this.hrControl;
      case "en": return this.enControl;
      case "it": return this.itControl;
      case "slo": return this.sloControl;
      case "sr": return this.srControl;
      case "bs": return this.bsControl;
      default: return undefined;
    }
  }
}